import {
  ALargeSmall,
  ArrowDownToLine,
  Backpack,
  Brain,
  Check,
  Dumbbell,
  Gem,
  HeartPulse,
  LogIn,
  Palette,
  Recycle,
  Settings2,
  Shield,
  ShieldCheck,
  Sparkles,
  Sun,
  UserRound,
  Volume2,
  VolumeX,
  WandSparkles,
  Zap,
} from "lucide-react";
import { useState } from "react";
import {
  type EffectChannel,
  type EffectSettings,
  EFFECT_CHANNELS,
  EFFECT_CHANNEL_LABELS,
  EFFECT_INTENSITY_MAX,
  EFFECT_INTENSITY_MIN,
  EFFECT_INTENSITY_STEP,
  normalizeEffectIntensity,
} from "../effects";
import {
  type ThemeId,
  FONT_SCALE_MAX,
  FONT_SCALE_MIN,
  FONT_SCALE_STEP,
  THEME_OPTIONS,
} from "../theme";
import {
  type AutoSellQuality,
  type GrowthProfile,
  type InventoryView,
  type MeResponse,
  type SocialProfile,
  type TaskBoard,
} from "../types";
import { EMPTY_PROGRESSION } from "../ui/defaults";
import { type GuildActions } from "../ui/navigation";
import { PageSections } from "../ui/PageSections";
import { Meter } from "../ui/Resources";
import { SocialPanel } from "./SocialPanel";
import { TaskPanel } from "./TaskPanel";

export function ProfileScreen({
  sectionOverride,
  me,
  inventory,
  growth,
  social,
  tasks,
  busy,
  theme,
  fontScale,
  effectSettings,
  muted,
  colorBlind,
  onTheme,
  onFontScale,
  onEffectChannel,
  onToggleMuted,
  onToggleColorBlind,
  onAutoSellQuality,
  onClaimTask,
  onClaimAllTasks,
  onFriendRequest,
  onFriendRespond,
  onFriendRemove,
  onCrewCreate,
  onCrewJoin,
  onCrewLeave,
  onCrewSupply,
  guildActions,
  onAccount,
  onAdmin,
}: {
  sectionOverride?: string;
  me: MeResponse;
  inventory: InventoryView;
  growth: GrowthProfile;
  social: SocialProfile | null;
  tasks: TaskBoard | null;
  busy: boolean;
  theme: ThemeId;
  fontScale: number;
  effectSettings: EffectSettings;
  muted: boolean;
  colorBlind: boolean;
  onTheme: (theme: ThemeId) => void;
  onFontScale: (scale: number) => void;
  onEffectChannel: (channel: EffectChannel, patch: Partial<EffectSettings[EffectChannel]>) => void;
  onToggleMuted: () => void;
  onToggleColorBlind: () => void;
  onAutoSellQuality: (quality: AutoSellQuality) => void;
  onClaimTask: (taskId: string) => void;
  onClaimAllTasks: () => void;
  onFriendRequest: (username: string) => void;
  onFriendRespond: (userId: string, accept: boolean) => void;
  onFriendRemove: (userId: string) => void;
  onCrewCreate: (name: string) => void;
  onCrewJoin: (name: string) => void;
  onCrewLeave: () => void;
  onCrewSupply: () => void;
  guildActions: GuildActions;
  onAccount: () => void;
  onAdmin: () => void;
}) {
  const progression = me.progression ?? inventory.progression ?? EMPTY_PROGRESSION;
  const levelPercent =
    progression.nextLevelExperience > 0
      ? (progression.currentLevelExperience / progression.nextLevelExperience) * 100
      : 100;
  const hp = Math.round(me.player.hpRatio * me.player.maxHp);
  const [selectedSection, setSection] = useState("character");
  const section = sectionOverride ?? selectedSection;
  return (
    <section className="profile-screen" aria-labelledby="profile-title">
      <header className="hub-page-header">
        <div>
          <h1 id="profile-title">
            {sectionOverride === "tasks" ? "任务" : sectionOverride === "social" ? "社交" : "我的"}
          </h1>
        </div>
        <span className="profile-header-tools">
          <span className="profile-diamond-balance" title={`钻石 ${me.player.diamond}`}>
            <Gem size={16} aria-hidden="true" />
            <strong>{me.player.diamond.toLocaleString("zh-CN")}</strong>
          </span>
          <button
            className="icon-button"
            onClick={onAccount}
            aria-label="账号管理"
            title="账号管理"
          >
            <UserRound size={18} />
          </button>
        </span>
      </header>
      {!sectionOverride && (
        <PageSections
          label="个人页面"
          value={section}
          onChange={setSection}
          items={[
            { id: "character", label: "人物", icon: <UserRound size={17} /> },
            { id: "settings", label: "设置", icon: <Settings2 size={17} /> },
          ]}
        />
      )}
      {section === "character" && (
        <>
          <div className="profile-identity">
            <img src="/assets/sprites/breacher.png" alt="" />
            <div>
              <small>{me.user.guest ? "游客账号" : `@${me.user.username}`}</small>
              <strong>{me.user.nickname || "Breacher"}</strong>
              <span>人物等级 {progression.level}</span>
            </div>
          </div>
          <div className="profile-progress">
            <div className="readout-label">
              <span>升级进度</span>
              <strong>
                {progression.currentLevelExperience} / {progression.nextLevelExperience}
              </strong>
            </div>
            <Meter value={levelPercent} tone="xp" />
          </div>
          <div className="profile-attributes" aria-label="属性等级">
            <span
              title={`剑消除经验 ${progression.attack.currentLevelExperience}/${progression.attack.nextLevelExperience || "满级"}`}
            >
              <Dumbbell size={17} />
              <small>攻击</small>
              <strong>{progression.attack.level}</strong>
            </span>
            <span
              title={`盾消除经验 ${progression.defense.currentLevelExperience}/${progression.defense.nextLevelExperience || "满级"}`}
            >
              <Shield size={17} />
              <small>防御</small>
              <strong>{progression.defense.level}</strong>
            </span>
            <span
              title={`杖消除经验 ${progression.intelligence.currentLevelExperience}/${progression.intelligence.nextLevelExperience || "满级"}`}
            >
              <Brain size={17} />
              <small>智力</small>
              <strong>{progression.intelligence.level}</strong>
            </span>
            <span
              title={`受伤与遭遇经验 ${progression.constitution.currentLevelExperience}/${progression.constitution.nextLevelExperience || "满级"}`}
            >
              <HeartPulse size={17} />
              <small>体质</small>
              <strong>{progression.constitution.level}</strong>
            </span>
          </div>
          <div className="profile-records">
            <span>
              <HeartPulse size={17} />
              <small>当前生命</small>
              <strong>
                {hp} / {me.player.maxHp}
              </strong>
            </span>
            <span>
              <ArrowDownToLine size={17} />
              <small>最深记录</small>
              <strong>{growth.bestDepth} 层</strong>
            </span>
            <span>
              <Backpack size={17} />
              <small>装备背包</small>
              <strong>
                {inventory.items.length} / {inventory.capacity}
              </strong>
            </span>
          </div>
        </>
      )}
      {section === "tasks" &&
        (tasks ? (
          <TaskPanel board={tasks} busy={busy} onClaim={onClaimTask} onClaimAll={onClaimAllTasks} />
        ) : (
          <p className="section-empty" role="status">
            任务暂不可用
          </p>
        ))}
      {section === "settings" && (
        <>
          <section className="profile-settings" aria-labelledby="profile-settings-title">
            <header>
              <Settings2 size={17} />
              <strong id="profile-settings-title">设置</strong>
            </header>
            <div className="theme-setting">
              <span>
                <Palette size={17} />
                <strong>界面主题</strong>
              </span>
              <div className="theme-options" role="radiogroup" aria-label="界面主题">
                {THEME_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={theme === option.id}
                    className={theme === option.id ? "is-active" : ""}
                    onClick={() => onTheme(option.id)}
                  >
                    <i className={`theme-swatch theme-swatch-${option.id}`} aria-hidden="true">
                      <b />
                      <b />
                      <b />
                    </i>
                    <span>{option.label}</span>
                    {theme === option.id && <Check size={13} aria-hidden="true" />}
                  </button>
                ))}
              </div>
            </div>
            <div className="font-size-setting">
              <label htmlFor="font-size-range">
                <span>
                  <ALargeSmall size={17} />
                  <strong>字体大小</strong>
                </span>
                <output htmlFor="font-size-range">{Math.round(fontScale * 100)}%</output>
              </label>
              <div className="font-size-control">
                <span>{Math.round(FONT_SCALE_MIN * 100)}%</span>
                <input
                  id="font-size-range"
                  type="range"
                  min={FONT_SCALE_MIN}
                  max={FONT_SCALE_MAX}
                  step={FONT_SCALE_STEP}
                  value={fontScale}
                  onChange={(event) => onFontScale(event.currentTarget.valueAsNumber)}
                  aria-label="字体大小"
                  aria-valuetext={`${Math.round(fontScale * 100)}%`}
                />
                <span>{Math.round(FONT_SCALE_MAX * 100)}%</span>
              </div>
            </div>
            <section className="effect-settings" aria-labelledby="effect-settings-title">
              <header>
                <Sparkles size={17} />
                <strong id="effect-settings-title">特效反馈</strong>
              </header>
              {EFFECT_CHANNELS.map((channel) => {
                const setting = effectSettings[channel];
                const rangeId = `effect-${channel}-range`;
                const icon =
                  channel === "impact" ? (
                    <Zap size={16} />
                  ) : channel === "particles" ? (
                    <WandSparkles size={16} />
                  ) : channel === "lighting" ? (
                    <Sun size={16} />
                  ) : (
                    <Sparkles size={16} />
                  );
                return (
                  <div
                    className={`effect-setting-row${setting.enabled ? " is-enabled" : ""}`}
                    key={channel}
                  >
                    <label className="effect-setting-toggle" htmlFor={`${rangeId}-toggle`}>
                      {icon}
                      <span>
                        <strong>{EFFECT_CHANNEL_LABELS[channel]}</strong>
                        <small>{setting.enabled ? `${setting.intensity}%` : "关闭"}</small>
                      </span>
                      <input
                        id={`${rangeId}-toggle`}
                        type="checkbox"
                        checked={setting.enabled}
                        onChange={(event) =>
                          onEffectChannel(channel, { enabled: event.currentTarget.checked })
                        }
                      />
                      <i aria-hidden="true" />
                    </label>
                    <input
                      id={rangeId}
                      className="effect-setting-range"
                      type="range"
                      min={EFFECT_INTENSITY_MIN}
                      max={EFFECT_INTENSITY_MAX}
                      step={EFFECT_INTENSITY_STEP}
                      value={setting.intensity}
                      disabled={!setting.enabled}
                      onChange={(event) =>
                        onEffectChannel(channel, {
                          intensity: normalizeEffectIntensity(event.currentTarget.valueAsNumber),
                        })
                      }
                      aria-label={`${EFFECT_CHANNEL_LABELS[channel]}强度`}
                      aria-valuetext={`${setting.intensity}%`}
                    />
                  </div>
                );
              })}
            </section>
            <label className="auto-sell-setting" htmlFor="auto-sell-quality">
              <span>
                <Recycle size={17} />
                <strong>撤离自动出售</strong>
              </span>
              <select
                id="auto-sell-quality"
                value={me.player.autoSellQuality ?? ""}
                disabled={busy}
                onChange={(event) =>
                  onAutoSellQuality(event.currentTarget.value as AutoSellQuality)
                }
              >
                <option value="">关闭</option>
                <option value="common">白色及以下</option>
                <option value="uncommon">绿色及以下</option>
                <option value="rare">蓝色及以下</option>
                <option value="epic">紫色及以下</option>
              </select>
            </label>
            <div className="setting-toggles">
              <label>
                {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
                <span>
                  <strong>游戏声音</strong>
                  <small>{muted ? "关闭" : "开启"}</small>
                </span>
                <input
                  type="checkbox"
                  checked={!muted}
                  onChange={onToggleMuted}
                  aria-label="游戏声音"
                />
                <i aria-hidden="true" />
              </label>
              <label>
                <Gem size={17} />
                <span>
                  <strong>色盲标记</strong>
                  <small>{colorBlind ? "开启" : "关闭"}</small>
                </span>
                <input
                  type="checkbox"
                  checked={colorBlind}
                  onChange={onToggleColorBlind}
                  aria-label="色盲标记"
                />
                <i aria-hidden="true" />
              </label>
            </div>
          </section>
          {me.user.role === "admin" && (
            <section className="admin-entry" aria-labelledby="admin-entry-title">
              <header>
                <ShieldCheck size={17} />
                <strong id="admin-entry-title">管理控制台</strong>
                <small>管理员</small>
              </header>
              <button className="secondary-command" onClick={onAdmin}>
                <ShieldCheck size={17} />
                查看玩家与发放资源
              </button>
            </section>
          )}
          <details className="asset-credits">
            <summary>素材与字体</summary>
            <p>
              像素美术由 Breacher 项目通过 PixelLab 制作。中文字体使用 Ark Pixel，数字字体使用
              Silkscreen，均为 SIL OFL 1.1。
            </p>
            <p>游戏音效由 Kenney 制作，采用 CC0 1.0 授权。</p>
          </details>
        </>
      )}
      {section === "social" &&
        (social ? (
          <SocialPanel
            profile={social}
            busy={busy}
            onFriendRequest={onFriendRequest}
            onFriendRespond={onFriendRespond}
            onFriendRemove={onFriendRemove}
            onCrewCreate={onCrewCreate}
            onCrewJoin={onCrewJoin}
            onCrewLeave={onCrewLeave}
            onCrewSupply={onCrewSupply}
            guildActions={guildActions}
          />
        ) : (
          <p className="section-empty" role="status">
            社交暂不可用
          </p>
        ))}
      {section === "character" && (
        <footer className="profile-account">
          <button
            className={me.user.guest ? "primary-command" : "secondary-command"}
            onClick={onAccount}
          >
            {me.user.guest ? <LogIn size={18} /> : <UserRound size={18} />}
            {me.user.guest ? "注册账号" : "账号管理"}
          </button>
        </footer>
      )}
    </section>
  );
}
